import React, { useEffect, useMemo, useState } from 'react';
import { SortBar } from '@teko/ecommerce';
import { FilterInterface } from '@teko/types/ecommerce';
import { ParsedUrlQueryInput } from 'querystring';
import { useRouter } from 'next/router';
import {
  useDiscoverySearch,
  useNewProductCard,
  useTenantConfig,
  useFilter,
  usePos365StoreCode,
} from 'hooks';
import { VerticalFilter } from '@teko/teko-ecommerce';
import {
  Col,
  Row,
  styled,
  Pagination,
  Container,
  ContainerProps,
  Color,
  SubTitle,
} from '@teko/ds-desktop';
import dynamic from 'next/dynamic';
import { getSearchVariables, cookFilterPrice, omitQuery } from 'helpers';
import { stringToInteger, scheduleTaskInIdleTime } from '@teko/utils';
import { trackViewProductList } from '@teko/tracking';
import { DiscoveryProduct, SearchProductPagination } from '@teko/discovery';

const NoProductsFound = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.NoProductsFound),
);

const ProductList = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ProductList),
);

const ProductGrid = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ProductGrid),
);

const Filter = dynamic(() =>
  import('@teko/ecommerce').then((mod) => mod.Filter),
);

const DiscoveryRecommendedProducts = dynamic(() =>
  import('components/DiscoveryRecommendedProducts').then(
    (mod) => mod.DiscoveryRecommendedProducts,
  ),
);

const ProductListWrapper = styled.div`
  background: #fff;
  width: 100%;
  border-radius: 0.5rem;
`;

const StyledPagination = styled(Pagination)`
  margin: auto;
`;

const StyledSortBar = styled(SortBar)`
  padding: 0 1rem;
`;

const StyledNoProductsFound = styled(NoProductsFound)`
  margin-top: 4rem;
  margin-bottom: 4rem;
`;

const StyledContainer = styled(Container)`
  max-width: 100%;
  margin-bottom: 24px;
`;

const PaginationItem = styled.a<{
  active?: boolean;
  disabled?: boolean;
  color?: string | Color;
}>(
  ({ theme, active, disabled, color }) => `
  border-radius: ${theme?.borderRadius.s};
  background-color: ${
    active ? theme.colors?.[color as Color] || color : theme?.colors.white
  };
  border: ${active ? 'none' : `1px solid ${theme?.colors.divider}`};
  pointer-events: ${disabled ? 'none' : 'auto'};
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content:center;
  color: ${active ? theme?.colors.white : 'currentColor'};
  text-decoration: none;
`,
);

interface ProductListWithFilterProps extends Omit<ContainerProps, 'children'> {
  filterParams?: ParsedUrlQueryInput;
  params: ParsedUrlQueryInput;
  serverProducts?: DiscoveryProduct[];
  serverPagination?: SearchProductPagination;
  regionName?: string;
  cookedFilters?: FilterInterface[];
  filterPrice?: {
    max: number;
    min: number;
  };
}
export const ProductListWithFilter = ({
  filterParams,
  params,
  serverProducts = [],
  serverPagination = { totalItems: 0, totalPages: 1 },
  cookedFilters = [],
  filterPrice,
  ...props
}: ProductListWithFilterProps) => {
  const { convertNewProductCard } = useNewProductCard();
  const { pos365StoreCode } = usePos365StoreCode();
  const router = useRouter();
  const {
    terminal,
    filterType = 'horizontal',
    isMobile,
    location,
    isBot,
  } = useTenantConfig();

  const currentQuery = router.query;
  const defaultItemsPerPage = 40;

  // to avoid sending duplicate GTM events
  const [, setProductSkuList] = useState<string[]>([]);

  const {
    displayFilters,
    activeFilterOptions,
    onClickFilterOption,
    onSortByPrice,
    onSortOptionSelected,
    onChangePage,
    clearFilterLink,
  } = useFilter(cookedFilters, { autoSubmit: true });

  const searchVariables = getSearchVariables({
    terminal: terminal,
    pos365StoreCode,
    isMobile,
    isBot,
    query: params,
    location,
    returnFilter: false,
  });

  const { result, isLoading } = useDiscoverySearch(searchVariables, {
    fallbackData: {
      products: serverProducts,
      pagination: serverPagination,
      filter: null,
      suggestedKeyword: undefined,
    },
  });
  const { products, pagination } = result;

  // send GTM track view product list event
  useEffect(() => {
    scheduleTaskInIdleTime(() => {
      setProductSkuList((prevSkuList) => {
        const newSkuList = products.map((product) => product.productInfo.sku);

        // check if these 2 arrays are different
        const different =
          prevSkuList.length !== newSkuList.length ||
          prevSkuList.find((sku) => !newSkuList.includes(sku));

        if (different) {
          trackViewProductList(
            products.map((product) => ({
              sku: product.productInfo?.sku || '',
              category: product.productInfo?.categories?.[0]?.name || '',
            })),
          );
          return newSkuList;
        }
        return prevSkuList;
      });
    }, 3000);
  }, [products]);

  const { price_gte = '', price_lte = '' } = params;
  const { priceGte, priceLte, priceFilter } = useMemo(() => {
    const priceFilter = cookFilterPrice(filterPrice?.max);
    return {
      priceGte: stringToInteger(price_gte.toString()) || priceFilter.min,
      priceLte: stringToInteger(price_lte.toString()) || priceFilter.max,
      priceFilter,
    };
  }, [price_gte, price_lte, filterPrice?.max]);

  const renderProductList = () => (
    <ProductListWrapper>
      <StyledSortBar
        onSortByPrice={filterType === 'horizontal' && onSortByPrice}
        onClickOption={onSortOptionSelected}
      />
      {!products.length && !isLoading && (
        <>
          <StyledNoProductsFound show={!isLoading && !products.length} />
          <StyledContainer>
            <DiscoveryRecommendedProducts />
          </StyledContainer>
        </>
      )}
      {!!pos365StoreCode ? (
        <ProductList
          products={products.map((product) => convertNewProductCard(product))}
          regionName="itemProductResult"
        />
      ) : (
        <ProductGrid
          products={products.map((product) => convertNewProductCard(product))}
          regionName="itemProductResult"
        />
      )}
    </ProductListWrapper>
  );
  const renderPagination = () =>
    products.length ? (
      <StyledPagination
        totalRecords={pagination.totalItems || 0}
        pageLimit={
          Math.ceil(pagination.totalItems / pagination.totalPages) ||
          defaultItemsPerPage
        }
        pageNeighbor={2}
        currentPage={parseInt(
          currentQuery.page ? currentQuery.page.toString() : '1',
        )}
        onPageChange={onChangePage}
        itemRender={{
          page: (page, state) => (
            <PaginationItem
              {...state}
              onClick={(e) => {
                e.preventDefault();
                !state.disabled && onChangePage(Number(page));
              }}
              href={`${router.asPath.replace(
                // remove page query param
                /([\?\&](?:\w+=\w*)+)/g,
                '',
              )}?${new URLSearchParams({
                ...omitQuery(router.query),
                page: page.toString(),
              })}`}
            >
              <SubTitle>{page}</SubTitle>
            </PaginationItem>
          ),
        }}
      />
    ) : null;

  const renderHorizontalFilterLayout = () => (
    <Container {...props}>
      {!!displayFilters.length && (
        <Row>
          <Col span={12}>
            <Filter
              query={currentQuery}
              onClickOption={onClickFilterOption}
              filters={displayFilters}
              activeFilters={activeFilterOptions}
              clearFilterLink={clearFilterLink as string}
            />
          </Col>
        </Row>
      )}
      <Row marginTop="s16">
        <Col span={12}>{renderProductList()}</Col>
      </Row>
      <Row marginTop="s16" marginBottom="s16">
        {renderPagination()}
      </Row>
    </Container>
  );
  const renderVerticalFilterLayout = () => (
    <Container {...props}>
      <Row gutter={[16, 16]} marginBottom="s16">
        <Col span={2}>
          <VerticalFilter
            activeOptions={activeFilterOptions}
            filters={displayFilters}
            onClickOption={onClickFilterOption}
            clearFilterLink={{
              href: clearFilterLink as string,
            }}
            filterPrice={
              priceFilter
                ? {
                    max: priceFilter.max,
                    min: priceFilter.min,
                    step: priceFilter.step,
                    value: {
                      gte: priceGte,
                      lte: priceLte,
                    },
                    onFilter: onSortByPrice,
                  }
                : null
            }
          />
        </Col>
        <Col span={10}>
          {renderProductList()}
          <Row marginTop="s16">{renderPagination()}</Row>
        </Col>
      </Row>
    </Container>
  );
  if (filterType === 'horizontal') return renderHorizontalFilterLayout();
  return renderVerticalFilterLayout();
};
